import { useEffect, useState } from 'react';
import { api, readErr } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/toast';
import {
    Gift, Copy, Share2, Users, Sparkles, CheckCircle2, Clock, Ban, Loader2,
} from 'lucide-react';

/**
 * Round 37 — Referrals page (wallet framework).
 *
 * Product rules surfaced here:
 *   - Shareable link: {FRONTEND_URL}/?ref={CODE}
 *   - Pre-composed WhatsApp value-prop message (copy-ready)
 *   - ₹100 wallet credit awarded to the REFERRER on every referee's
 *     first wallet recharge of ≥ ₹25.
 *   - No cap.
 */
export default function Referrals() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        api.get('/referral/me')
            .then((r) => alive && setData(r.data))
            .catch((e) => toast.error(readErr(e, 'Failed to load referrals')))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, []);

    const copy = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied`);
        } catch {
            toast.error('Copy failed — long-press to select instead');
        }
    };

    const shareWhatsApp = () => {
        if (!data) return;
        const url = `https://wa.me/?text=${encodeURIComponent(data.share_message)}`;
        window.open(url, '_blank', 'noopener');
    };

    const nativeShare = async () => {
        if (!data) return;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'AstroConsult',
                    text: data.share_message,
                    url: data.share_link,
                });
            } catch { /* cancelled */ }
        } else {
            copy(data.share_message, 'Message');
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="referrals-loading">
                <Loader2 className="animate-spin text-marigold" size={24} />
            </div>
        );
    }
    if (!data) return null;

    const { code, share_link, share_message, reward_inr, reward_label,
            currency, min_recharge, stats, referrals } = data;
    // Round 40l — reward is currency-aware. INR sharers see ₹100, USD sharers see $5.
    // `reward_label` is the canonical pre-formatted string from the API;
    // fall back to building it client-side for older API responses.
    const rewardLabel = reward_label
        || (currency === 'USD'
            ? `$${Number(reward_inr || 0).toFixed(2)}`
            : `₹${Number(reward_inr || 0).toLocaleString('en-IN')}`);

    return (
        <div className="max-w-4xl mx-auto p-6 lg:p-10 space-y-8" data-testid="referrals-page">
            {/* Hero */}
            <header className="space-y-3">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-marigold/10 border border-marigold/30 text-marigold text-xs uppercase tracking-wider">
                    <Gift size={14} /> Refer &amp; Earn
                </div>
                <h1 className="font-serif text-4xl lg:text-5xl text-parchment leading-tight" data-testid="referrals-heading">
                    Invite friends. Earn <span className="text-marigold">{rewardLabel}</span> wallet credit on every qualifying recharge.
                </h1>
                <p className="text-slate-400 text-base max-w-2xl">
                    Share your link below. When a friend signs up and makes their first
                    wallet recharge of <strong className="text-parchment">{min_recharge}</strong> or more,
                    we credit your wallet with <strong className="text-marigold">{rewardLabel}</strong> —
                    instantly. No cap on referrals.
                </p>
            </header>

            {/* Stats */}
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="referrals-stats">
                <StatCard icon={Users} label="Invited" value={stats.total_invited} testId="stat-invited" />
                <StatCard icon={CheckCircle2} label="Qualified" value={stats.qualified} testId="stat-qualified" accent />
                <StatCard icon={Clock} label="Pending" value={stats.pending} testId="stat-pending" />
                <StatCard
                    icon={Sparkles}
                    label="Wallet earned"
                    value={currency === 'USD'
                        ? `$${Number(stats.earned_inr || 0).toFixed(2)}`
                        : `₹${Number(stats.earned_inr || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`}
                    testId="stat-earned"
                    accent
                />
            </section>

            {/* Your code / link */}
            <Card className="bg-surface/50 border-marigold/15 p-6 lg:p-8 space-y-5 rounded-none">
                <div>
                    <div className="overline mb-3">Your code</div>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="font-mono text-3xl lg:text-4xl text-marigold tracking-widest" data-testid="referral-code">
                            {code}
                        </div>
                        <Button
                            onClick={() => copy(code, 'Code')}
                            variant="outline"
                            className="border-marigold/40 text-marigold hover:bg-marigold/10 rounded-none h-10"
                            data-testid="copy-code-btn"
                        >
                            <Copy size={16} className="mr-2" /> Copy
                        </Button>
                    </div>
                </div>

                <div className="border-t border-marigold/10 pt-5">
                    <div className="overline mb-3">Your shareable link</div>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <code className="text-sm text-slate-300 break-all flex-1 min-w-0" data-testid="referral-link">
                            {share_link}
                        </code>
                        <Button
                            onClick={() => copy(share_link, 'Link')}
                            variant="outline"
                            className="border-marigold/40 text-marigold hover:bg-marigold/10 rounded-none h-10 shrink-0"
                            data-testid="copy-link-btn"
                        >
                            <Copy size={16} className="mr-2" /> Copy
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Pre-composed WA message */}
            <Card className="bg-surface/50 border-marigold/15 p-6 lg:p-8 space-y-4 rounded-none">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <div className="overline mb-2">WhatsApp-ready message</div>
                        <p className="text-sm text-slate-400">
                            Pre-written with the value prop. Copy and paste into any chat or group.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            onClick={() => copy(share_message, 'Message')}
                            variant="outline"
                            className="border-marigold/40 text-marigold hover:bg-marigold/10 rounded-none h-10"
                            data-testid="copy-message-btn"
                        >
                            <Copy size={16} className="mr-2" /> Copy
                        </Button>
                        <Button
                            onClick={shareWhatsApp}
                            className="bg-[#25D366] text-white hover:bg-[#1FB855] rounded-none h-10"
                            data-testid="share-whatsapp-btn"
                        >
                            <Share2 size={16} className="mr-2" /> WhatsApp
                        </Button>
                    </div>
                </div>
                <pre className="whitespace-pre-wrap font-sans text-sm text-parchment bg-obsidian/50 p-4 border-l-2 border-marigold/40" data-testid="share-message-preview">
                    {share_message}
                </pre>
                <button
                    onClick={nativeShare}
                    className="text-xs text-slate-500 hover:text-marigold uppercase tracking-wider"
                    data-testid="native-share-btn"
                >
                    <Share2 size={12} className="inline mr-1" /> Or use device share sheet
                </button>
            </Card>

            {/* Referrals list */}
            <section data-testid="referrals-list-section">
                <h2 className="font-serif text-2xl text-parchment mb-4">Your referrals</h2>
                {referrals.length === 0 ? (
                    <Card className="bg-surface/30 border-marigold/10 p-8 text-center rounded-none" data-testid="referrals-empty">
                        <p className="text-slate-400">No invites yet. Share your link to get started.</p>
                    </Card>
                ) : (
                    <Card className="bg-surface/50 border-marigold/15 rounded-none overflow-hidden">
                        <ul className="divide-y divide-marigold/10" data-testid="referrals-list">
                            {referrals.map((r, i) => (
                                <ReferralRow key={i} r={r} testId={`referral-row-${i}`} />
                            ))}
                        </ul>
                    </Card>
                )}
            </section>
        </div>
    );
}


function StatCard({ icon: Icon, label, value, accent, testId }) {
    return (
        <Card
            className={`p-4 rounded-none border-marigold/15 ${accent ? 'bg-marigold/5' : 'bg-surface/40'}`}
            data-testid={testId}
        >
            <div className="flex items-center gap-2 text-slate-400 mb-1">
                <Icon size={14} />
                <span className="text-xs uppercase tracking-wider">{label}</span>
            </div>
            <div className={`text-2xl font-serif ${accent ? 'text-marigold' : 'text-parchment'}`}>
                {value}
            </div>
        </Card>
    );
}


function ReferralRow({ r, testId }) {
    const statusConfig = {
        qualified: { icon: CheckCircle2, label: 'Credited', color: 'text-emerald-400' },
        pending:   { icon: Clock,        label: 'Awaiting first recharge', color: 'text-marigold' },
        rejected:  { icon: Ban,          label: r.rejection_reason || 'Rejected', color: 'text-slate-500' },
    };
    const cfg = statusConfig[r.status] || statusConfig.pending;
    const Icon = cfg.icon;
    const when = r.signup_at ? new Date(r.signup_at).toLocaleDateString() : '';

    return (
        <li className="flex items-center justify-between gap-3 p-4 flex-wrap" data-testid={testId}>
            <div className="min-w-0">
                <div className="text-parchment truncate">{r.email}</div>
                <div className="text-xs text-slate-500">Signed up {when}</div>
            </div>
            <div className={`flex items-center gap-2 text-sm ${cfg.color}`}>
                <Icon size={14} />
                <span>{cfg.label}</span>
                {r.status === 'qualified' && Number(r.reward_inr || 0) > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-marigold/15 text-marigold text-xs rounded-full">
                        +{(r.reward_currency === 'USD')
                            ? `$${Number(r.reward_inr).toFixed(2)}`
                            : `₹${Number(r.reward_inr).toLocaleString('en-IN')}`}
                    </span>
                )}
            </div>
        </li>
    );
}
